import { useState, useEffect, useCallback } from 'react';
import * as aboutUsService from '../services/aboutUsService';
import * as mediaService from '../services/mediaService';
import { playSuccessChime, playSoftPop } from '../utils/audio';

/**
 * useAboutUs Hook
 * Manages AboutUs entities, form inputs, dynamic image uploads, and sound feedback.
 */
export function useAboutUs() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Modals state: 'add' | 'edit' | 'delete' | null
  const [activeModal, setActiveModal] = useState(null);
  const [selectedAbout, setSelectedAbout] = useState(null);

  // Form Fields
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);

  // Fetch all items
  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await aboutUsService.getAllAboutUs();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Biz haqimizda ma\'lumotlarini yuklashda xatolik yuz berdi.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Open modal handlers
  const openAddModal = useCallback(() => {
    playSoftPop();
    setTitle('');
    setText('');
    setMediaUrls([]);
    setFieldErrors({});
    setError('');
    setActiveModal('add');
  }, []);

  const openEditModal = useCallback((item) => {
    playSoftPop();
    setSelectedAbout(item);
    setTitle(item.title || '');
    setText(item.text || '');
    setMediaUrls(Array.isArray(item.mediaUrls) ? [...item.mediaUrls] : []);
    setFieldErrors({});
    setError('');
    setActiveModal('edit');
  }, []);

  const openDeleteModal = useCallback((item) => {
    playSoftPop();
    setSelectedAbout(item);
    setError('');
    setActiveModal('delete');
  }, []);

  const closeModal = useCallback(() => {
    playSoftPop();
    setActiveModal(null);
    setSelectedAbout(null);
    setTitle('');
    setText('');
    setMediaUrls([]);
    setFieldErrors({});
    setError('');
  }, []);

  // Media upload handler
  const handleUploadImage = async (file) => {
    if (!file) return;
    setIsUploading(true);
    setError('');
    try {
      const url = await mediaService.uploadImage(file);
      if (url) {
        setMediaUrls((prev) => [...prev, url]);
        playSuccessChime();
      } else {
        setError('Rasmni yuklashda xatolik yuz berdi.');
      }
    } catch (err) {
      console.error(err);
      setError('Serverga ulanishda xatolik yuz berdi.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    playSoftPop();
    setMediaUrls((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // Validation
  const validateForm = useCallback(() => {
    const errors = {};
    if (!title.trim()) errors.title = 'Sarlavha kiritilishi shart.';
    if (!text.trim()) errors.text = 'Tavsif kiritilishi shart.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [title, text]);

  // CRUD submit handlers
  const handleAddAboutUs = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');
    try {
      await aboutUsService.addAboutUs({
        title: title.trim(),
        text: text.trim(),
        mediaUrls,
      });
      playSuccessChime();
      await fetchItems();
      setActiveModal(null);
      setSelectedAbout(null);
      setTitle('');
      setText('');
      setMediaUrls([]);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Qo\'shishda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [title, text, mediaUrls, fetchItems, validateForm]);

  const handleEditAboutUs = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedAbout) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');
    try {
      await aboutUsService.updateAboutUs(selectedAbout.id, {
        title: title.trim(),
        text: text.trim(),
        mediaUrls,
      });
      playSuccessChime();
      await fetchItems();
      setActiveModal(null);
      setSelectedAbout(null);
      setTitle('');
      setText('');
      setMediaUrls([]);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Yangilashda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedAbout, title, text, mediaUrls, fetchItems, validateForm]);

  const handleDeleteAboutUs = useCallback(async () => {
    if (!selectedAbout) return;

    setIsSubmitting(true);
    setError('');
    try {
      await aboutUsService.deleteAboutUs(selectedAbout.id);
      playSuccessChime();
      await fetchItems();
      setActiveModal(null);
      setSelectedAbout(null);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'O\'chirishda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedAbout, fetchItems]);

  return {
    items,
    isLoading,
    isSubmitting,
    isUploading,
    error,
    fieldErrors,
    activeModal,
    selectedAbout,
    title,
    setTitle,
    text,
    setText,
    mediaUrls,
    openAddModal,
    openEditModal,
    openDeleteModal,
    closeModal,
    handleUploadImage,
    handleRemoveImage,
    handleAddAboutUs,
    handleEditAboutUs,
    handleDeleteAboutUs,
    fetchItems,
  };
}
